import { useApolloClient } from "@apollo/client";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { get, isEmpty } from "lodash";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { fetchZipCodeCities } from "shared/hooks/useZipCodeCities";
import { NAME_SPACES } from "shared/locales/constants";
import { isAcorisRiaBlocked } from "screens/Private/Broker/Projects/Detail/Templates/Comparison/Acoris/utils/riaBlock";
import {
  createEmptyAcorisTerminationRequest,
  resolveAcorisContractMoreThanOneYear,
  resolveAcorisHasOtherInsurer,
  resolveAcorisTerminationRequest,
} from "screens/Private/Broker/Projects/Detail/Templates/Comparison/Acoris/utils/terminationRequest";
import useAcorisZipAddress from "screens/Private/Broker/Projects/Detail/Templates/Comparison/Acoris/utils/useAcorisZipAddress";
import {
  ACORIS_ZIP_CODE_LENGTH,
  getAcorisCityPlaceholder,
} from "screens/Private/Broker/Projects/Detail/Templates/Comparison/Acoris/utils/zipCode";
import {
  ACORIS_SOCIAL_REGIMES,
  CONTACT_POINT_SYSTEM,
  DATE_FORMAT,
} from "utils/constants";
import {
  acorisMinBirthDateEndOfYear,
  hundredYearsAgoStart,
} from "utils/helpers/date";
import { bindDateInputProps, bindInputProps } from "utils/helpers/input";
import { mapSocialRegimeOptions } from "utils/helpers/socialRegime";
import { filterContractNumber } from "utils/helpers/validations";
import infoGradientIcon from "../Assets/icons/info_gradient.svg";
import ActionButtons from "../Components/ActionButtons";
import Header from "../Components/Header";
import Input from "../Components/Input";
import Layout from "../Components/Layout";
import Select from "../Components/Select";
import YesNoBlock from "../Components/YesNoBlock";
import validation from "./validation";

function View({
  telecoms,
  contact,
  contract,
  project,
  onNext,
  onBack,
  deltaStart,
  deltaRia,
  territorial,
}) {
  const client = useApolloClient();
  const { t } = useTranslation(NAME_SPACES.PUBLIC.ACORIS);
  const { t: tErrors } = useTranslation(NAME_SPACES.ERRORS);
  const TRANSLATION = t("ADHERENT_BIRTHDATE", {
    returnObjects: true,
  });
  const SOCIAL_REGIMES_TRANSLATION = t("SOCIAL_REGIMES", {
    returnObjects: true,
  });
  const QUESTIONS = TRANSLATION.QUESTIONS || {};

  const {
    submitForm,
    values,
    setFieldValue,
    isSubmitting,
    setSubmitting,
    ...formProps
  } = useFormik({
    initialValues: {
      telecoms,
      birthDate: get(contact, "user.birthDate") || null,
      socialRegime: get(contact, "socialRegime") || null,
      issuanceDate: get(contract, "issuanceDate") || null,
      optMadelin: get(contact, "optMadelin") || false,
      isClient: false,
      hasOtherInsurer: resolveAcorisHasOtherInsurer(project),
      replaceContract: get(contact, "fields.replaceContract") ?? false,
      contractOlderThanOneYear: resolveAcorisContractMoreThanOneYear(project),
      terminationRequest: resolveAcorisTerminationRequest(project),
    },
    onSubmit: onNext,
    validationSchema: validation(
      t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, { returnObjects: true }),
      {
        fetchZipCodeCities: (zip) => fetchZipCodeCities(client, zip),
      },
    ),
  });

  const subscriberZip = get(
    values,
    `telecoms.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`,
  );
  const { changeZip: changeSubscriberZip } = useAcorisZipAddress(subscriberZip);
  const {
    isForeignZip: isForeignTerminationZip,
    cities: destinationCities,
    citiesLoading: destinationCitiesLoading,
    changeZip: changeTerminationZip,
  } = useAcorisZipAddress(get(values, "terminationRequest.zipCode"));

  const showReplaceQuestion = values.hasOtherInsurer === true;
  const showOlderThanOneYear =
    showReplaceQuestion && values.replaceContract === true;
  const showContractDetails =
    showOlderThanOneYear && values.contractOlderThanOneYear === true;
  const effectiveDeltaStart = showContractDetails ? deltaRia : deltaStart;

  useEffect(() => {
    if (!showContractDetails) {
      return;
    }

    const nextMinimumIssuanceDate = dayjs()
      .add(deltaRia, "days")
      .startOf("day");
    const currentIssuanceDate = values.issuanceDate
      ? dayjs(values.issuanceDate)
      : null;

    if (
      !currentIssuanceDate?.isValid() ||
      currentIssuanceDate.isBefore(nextMinimumIssuanceDate, "day")
    ) {
      setFieldValue(
        "issuanceDate",
        nextMinimumIssuanceDate.add(2, "h").toISOString(),
      );
    }
  }, [showContractDetails, deltaRia, setFieldValue, values.issuanceDate]);

  const handleIsClient = async (value) => {
    if (isSubmitting) {
      return;
    }

    setFieldValue("isClient", value);
    if (!value) {
      return;
    }

    const nextValues = { ...values, isClient: true };
    const errors = await formProps.validateForm(nextValues);
    if (!isEmpty(errors)) {
      formProps.setTouched(
        Object.keys(errors).reduce((acc, key) => ({ ...acc, [key]: true }), {}),
      );
      return;
    }

    setSubmitting(true);
    try {
      await Promise.resolve(onNext(nextValues));
    } finally {
      setSubmitting(false);
    }
  };

  const clearTerminationDetails = () => {
    setFieldValue("terminationRequest", null);
  };

  const handleHasOtherInsurer = (value) => {
    setFieldValue("hasOtherInsurer", value);
    if (!value) {
      setFieldValue("replaceContract", false);
      setFieldValue("contractOlderThanOneYear", null);
      clearTerminationDetails();
      return;
    }
    setFieldValue("replaceContract", false);
    setFieldValue("contractOlderThanOneYear", null);
  };

  const handleReplaceContract = (value) => {
    setFieldValue("replaceContract", value);
    if (!value) {
      setFieldValue("contractOlderThanOneYear", null);
      clearTerminationDetails();
      return;
    }
    setFieldValue("contractOlderThanOneYear", null);
  };

  const handleContractOlderThanOneYear = (value) => {
    setFieldValue("contractOlderThanOneYear", value);
    if (!value) {
      clearTerminationDetails();
      return;
    }
    setFieldValue(
      "terminationRequest",
      values.terminationRequest || createEmptyAcorisTerminationRequest(),
    );
  };

  const riaBlocked = isAcorisRiaBlocked({
    hasOtherInsurer: values.replaceContract === true,
    contractOlderThanOneYear: values.contractOlderThanOneYear,
  });
  const isContinueDisabled =
    !get(values, `telecoms.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`) ||
    !values.birthDate ||
    !values.socialRegime ||
    !values.issuanceDate ||
    riaBlocked ||
    (showContractDetails &&
      (!get(values, "terminationRequest.dueDate") ||
        !get(values, "terminationRequest.contractNumber") ||
        !get(values, "terminationRequest.address") ||
        !get(values, "terminationRequest.zipCode") ||
        !get(values, "terminationRequest.city") ||
        !get(values, "terminationRequest.brokerageMandate.currentCompany"))) ||
    !isEmpty(formProps.errors);

  return (
    <Layout>
      <Header
        title={TRANSLATION.HEADER.TITLE}
        highlight={TRANSLATION.HEADER.HIGHLIGHT}
        subtitle={TRANSLATION.HEADER.SUBTITLE}
      />
      <div className="acoris-form">
        <div className="acoris-form__container">
          <div className="acoris-form__row">
            <Input
              placeholder={TRANSLATION.FORM.ZIP_CODE.PLACEHOLDER}
              maxLength={ACORIS_ZIP_CODE_LENGTH}
              label={TRANSLATION.FORM.ZIP_CODE.LABEL}
              {...bindInputProps({
                name: `telecoms.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`,
                values,
                ...formProps,
              })}
              onChange={({ target: { value } }) => {
                const { nextZip, reset } = changeSubscriberZip(value);
                setFieldValue(
                  `telecoms.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`,
                  nextZip,
                );
                setFieldValue(
                  `telecoms.${CONTACT_POINT_SYSTEM.CITY}.value`,
                  reset.city,
                );
                if (reset.country !== undefined) {
                  setFieldValue(
                    `telecoms.${CONTACT_POINT_SYSTEM.COUNTRY}.value`,
                    reset.country,
                  );
                }
              }}
            />
            <Input
              type="date"
              format={DATE_FORMAT}
              placeholder={TRANSLATION.FORM.BIRTH_DATE.PLACEHOLDER}
              label={TRANSLATION.FORM.BIRTH_DATE.LABEL}
              disabledDate={(current) =>
                current > acorisMinBirthDateEndOfYear ||
                current < hundredYearsAgoStart
              }
              {...(!get(values, "birthDate") && {
                defaultPickerValue: dayjs().subtract(30, "years"),
              })}
              {...bindDateInputProps({
                name: "birthDate",
                values,
                setFieldValue,
                ...formProps,
              })}
            />
          </div>

          <div className="acoris-form__row">
            <Select
              options={mapSocialRegimeOptions(
                ACORIS_SOCIAL_REGIMES,
                SOCIAL_REGIMES_TRANSLATION,
              ).filter(
                ({ value }) =>
                  !territorial ||
                  value !== ACORIS_SOCIAL_REGIMES.SECURITE_SOCIALE_INDEPENDANTS,
              )}
              placeholder={TRANSLATION.FORM.SOCIAL_REGIME.PLACEHOLDER}
              label={TRANSLATION.FORM.SOCIAL_REGIME.LABEL}
              value={values.socialRegime}
              touched={get(formProps.touched, "socialRegime", false)}
              errors={get(formProps.errors, "socialRegime", "")}
              onChange={(value) => {
                setFieldValue("socialRegime", value);
                if (
                  value !== ACORIS_SOCIAL_REGIMES.SECURITE_SOCIALE_INDEPENDANTS
                ) {
                  setFieldValue("optMadelin", false);
                }
              }}
              onBlur={() => {
                formProps.setFieldTouched("socialRegime", true);
              }}
            />
            <Input
              type="date"
              format={DATE_FORMAT}
              placeholder={TRANSLATION.FORM.EFFECTIVE_DATE.PLACEHOLDER}
              label={TRANSLATION.FORM.EFFECTIVE_DATE.LABEL}
              disabledDate={(current) =>
                current &&
                current <
                  dayjs().add(effectiveDeltaStart, "days").startOf("day")
              }
              {...bindDateInputProps({
                name: "issuanceDate",
                values,
                setFieldValue,
                ...formProps,
              })}
            />
          </div>

          <div className="acoris-form__questions">
            <YesNoBlock
              question={QUESTIONS.IS_CLIENT?.LABEL}
              highlight={QUESTIONS.IS_CLIENT?.HIGHLIGHT}
              value={values.isClient}
              onChange={handleIsClient}
              yesLabel={QUESTIONS.YES}
              noLabel={QUESTIONS.NO}
            />

            <YesNoBlock
              question={QUESTIONS.HAS_OTHER_INSURER?.LABEL}
              highlight={QUESTIONS.HAS_OTHER_INSURER?.HIGHLIGHT}
              value={values.hasOtherInsurer}
              onChange={handleHasOtherInsurer}
              yesLabel={QUESTIONS.YES}
              noLabel={QUESTIONS.NO}
              variant={showReplaceQuestion ? "card" : "pill"}
            >
              {showReplaceQuestion && (
                <>
                  <YesNoBlock
                    question={QUESTIONS.REPLACE_CONTRACT?.LABEL}
                    highlight={QUESTIONS.REPLACE_CONTRACT?.HIGHLIGHT}
                    value={values.replaceContract}
                    onChange={handleReplaceContract}
                    yesLabel={QUESTIONS.YES}
                    noLabel={QUESTIONS.NO}
                    variant="nested"
                  />

                  {showOlderThanOneYear && (
                    <YesNoBlock
                      question={QUESTIONS.OLDER_THAN_ONE_YEAR?.LABEL}
                      highlight={QUESTIONS.OLDER_THAN_ONE_YEAR?.HIGHLIGHT}
                      value={values.contractOlderThanOneYear}
                      onChange={handleContractOlderThanOneYear}
                      yesLabel={QUESTIONS.YES}
                      noLabel={QUESTIONS.NO}
                      variant="nested"
                      className={
                        showContractDetails
                          ? "acoris-yes-no--nested-card"
                          : undefined
                      }
                    >
                      {riaBlocked && (
                        <p className="acoris-form__alert-error">
                          {tErrors("VALIDATIONS.ACORIS.RIA_UNDER_ONE_YEAR")}
                        </p>
                      )}
                      {showContractDetails && (
                        <>
                          <div className="acoris-yes-no__details">
                            <div className="acoris-form__row acoris-form__row--single">
                              <Input
                                type="date"
                                format={DATE_FORMAT}
                                label={
                                  QUESTIONS.CURRENT_CONTRACT_EFFECT_DATE?.LABEL
                                }
                                placeholder={
                                  QUESTIONS.CURRENT_CONTRACT_EFFECT_DATE
                                    ?.PLACEHOLDER
                                }
                                defaultPickerValue={dayjs().subtract(1, "year")}
                                disabledDate={(current) =>
                                  current &&
                                  current > dayjs().subtract(1, "year")
                                }
                                {...bindDateInputProps({
                                  name: "terminationRequest.dueDate",
                                  values,
                                  setFieldValue,
                                  ...formProps,
                                })}
                              />
                            </div>
                            <div className="acoris-form__row">
                              <Input
                                label={
                                  QUESTIONS.CURRENT_CONTRACT_REFERENCE?.LABEL
                                }
                                placeholder={
                                  QUESTIONS.CURRENT_CONTRACT_REFERENCE
                                    ?.PLACEHOLDER
                                }
                                value={get(
                                  values,
                                  "terminationRequest.contractNumber",
                                  "",
                                )}
                                onChange={({ target: { value } }) => {
                                  setFieldValue(
                                    "terminationRequest.contractNumber",
                                    filterContractNumber(value),
                                  );
                                }}
                              />
                              <Input
                                label={QUESTIONS.CURRENT_INSURER_NAME?.LABEL}
                                placeholder={
                                  QUESTIONS.CURRENT_INSURER_NAME?.PLACEHOLDER
                                }
                                {...bindInputProps({
                                  name: "terminationRequest.brokerageMandate.currentCompany",
                                  values,
                                  setFieldValue,
                                  ...formProps,
                                })}
                              />
                            </div>
                            <div className="acoris-form__row acoris-form__row--single">
                              <Input
                                label={QUESTIONS.CURRENT_INSURER_ADDRESS?.LABEL}
                                placeholder={
                                  QUESTIONS.CURRENT_INSURER_ADDRESS?.PLACEHOLDER
                                }
                                {...bindInputProps({
                                  name: "terminationRequest.address",
                                  values,
                                  setFieldValue,
                                  ...formProps,
                                })}
                              />
                            </div>
                            <div className="acoris-form__row acoris-form__row--single">
                              <Input
                                label={
                                  QUESTIONS.CURRENT_INSURER_COMPLEMENT?.LABEL
                                }
                                placeholder={
                                  QUESTIONS.CURRENT_INSURER_COMPLEMENT
                                    ?.PLACEHOLDER
                                }
                                {...bindInputProps({
                                  name: "terminationRequest.complement",
                                  values,
                                  setFieldValue,
                                  ...formProps,
                                })}
                              />
                            </div>
                            <div className="acoris-form__row">
                              <Input
                                label={
                                  QUESTIONS.CURRENT_INSURER_ZIP_CODE?.LABEL
                                }
                                placeholder={
                                  QUESTIONS.CURRENT_INSURER_ZIP_CODE
                                    ?.PLACEHOLDER
                                }
                                maxLength={ACORIS_ZIP_CODE_LENGTH}
                                {...bindInputProps({
                                  name: "terminationRequest.zipCode",
                                  values,
                                  setFieldValue,
                                  ...formProps,
                                })}
                                onChange={({ target: { value } }) => {
                                  const { nextZip, reset } =
                                    changeTerminationZip(value);
                                  setFieldValue(
                                    "terminationRequest.zipCode",
                                    nextZip,
                                  );
                                  setFieldValue(
                                    "terminationRequest.city",
                                    reset.city || "",
                                  );
                                }}
                              />
                              {isForeignTerminationZip ? (
                                <Input
                                  label={QUESTIONS.CURRENT_INSURER_CITY?.LABEL}
                                  placeholder={getAcorisCityPlaceholder(
                                    QUESTIONS.CURRENT_INSURER_CITY,
                                    true,
                                  )}
                                  {...bindInputProps({
                                    name: "terminationRequest.city",
                                    values,
                                    setFieldValue,
                                    ...formProps,
                                  })}
                                />
                              ) : (
                                <Select
                                  showSearch
                                  label={QUESTIONS.CURRENT_INSURER_CITY?.LABEL}
                                  placeholder={
                                    QUESTIONS.CURRENT_INSURER_CITY?.PLACEHOLDER
                                  }
                                  loading={destinationCitiesLoading}
                                  options={destinationCities}
                                  value={
                                    get(values, "terminationRequest.city") ||
                                    undefined
                                  }
                                  touched={get(
                                    formProps.touched,
                                    "terminationRequest.city",
                                    false,
                                  )}
                                  errors={get(
                                    formProps.errors,
                                    "terminationRequest.city",
                                    "",
                                  )}
                                  onChange={(value) =>
                                    setFieldValue(
                                      "terminationRequest.city",
                                      value,
                                    )
                                  }
                                />
                              )}
                            </div>
                          </div>
                          <div className="acoris-yes-no__info">
                            <img src={infoGradientIcon} alt="" />
                            <p>{QUESTIONS.LEGAL_NOTICE}</p>
                          </div>
                        </>
                      )}
                    </YesNoBlock>
                  )}
                </>
              )}
            </YesNoBlock>
          </div>

          <ActionButtons
            disabled={isContinueDisabled || isSubmitting}
            onNext={submitForm}
            onBack={onBack}
            nextText={TRANSLATION.BUTTON?.CONTINUE || "Continuer"}
            backText={TRANSLATION.BUTTON?.BACK || "Retour"}
            nextProps={{ loading: isSubmitting }}
            backProps={{ disabled: isSubmitting }}
          />
        </div>
      </div>
    </Layout>
  );
}

export default View;
